import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, RefreshCcw, FlaskConical } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function EditorialMigrationAudit() {
  const nav = useNavigate();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creatingFixture, setCreatingFixture] = useState(false);
  const [fixtureResult, setFixtureResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/editorial/migration-audit");
      setAudit(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createE2EFixture = async () => {
    if (creatingFixture) return;
    const ok = window.confirm(
      "Создать один изолированный опубликованный тестовый материал для E2E? Реальные материалы не будут изменены."
    );
    if (!ok) return;
    setCreatingFixture(true);
    setError("");
    try {
      const { data } = await api.post("/admin/editorial/e2e-fixture");
      setFixtureResult(data);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setCreatingFixture(false);
    }
  };

  const counts = audit?.counts || {};
  const pii = audit?.pii_like_paths || [];
  const media = audit?.private_media_reference_paths || [];
  const mediaInventory = audit?.private_media_inventory || [];
  const mediaSummary = audit?.private_media_inventory_summary || {};

  return (
    <div className="min-h-screen bg-[#F7F1E7] text-[#3D2D23]" data-testid="editorial-migration-audit-page">
      <header className="border-b border-[#3D2D23]/10 bg-white/70 backdrop-blur">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-3">
          <button
            onClick={() => nav("/admin/editorial")}
            className="p-2 -ml-2 rounded-full hover:bg-[#EDE0CD]"
            aria-label="Назад в Редакцию"
            data-testid="editorial-migration-audit-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.28em] opacity-60">Anokina Studio · Owner</div>
            <h1 className="font-serif text-2xl">Проверка переноса Редакции</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={createE2EFixture}
              disabled={creatingFixture}
              data-testid="editorial-e2e-fixture-create"
            >
              <FlaskConical className="w-4 h-4 mr-1" />
              {creatingFixture ? "Создаём…" : "Создать E2E-материал"}
            </Button>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="editorial-migration-audit-refresh">
              <RefreshCcw className="w-4 h-4 mr-1" />
              Обновить
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8 space-y-6">
        <div className="rounded-2xl bg-white/70 border border-[#3D2D23]/10 p-5">
          <div className="text-sm font-medium">Режим проверки</div>
          <div className="mt-1 text-sm opacity-70">
            Только чтение. Никаких записей в International или RU.
          </div>
        </div>

        {fixtureResult && (
          <div className="rounded-2xl bg-white/80 border border-emerald-300 p-5" data-testid="editorial-e2e-fixture-result">
            <div className="font-medium">E2E-материал создан</div>
            <div className="mt-2 text-sm break-all">
              Material ID: <strong>{fixtureResult.material_id}</strong>
            </div>
            <div className="mt-1 text-xs opacity-65">
              Опубликован · без media · без product link · published_by = null
            </div>
          </div>
        )}

        {loading && (
          <div className="rounded-2xl bg-white/70 border border-[#3D2D23]/10 p-8 text-center opacity-70">
            Проверяем редакционный контент…
          </div>
        )}

        {error && (
          <div className="rounded-2xl bg-white/80 border border-red-300 p-5" data-testid="editorial-migration-audit-error">
            <div className="font-medium">Не удалось выполнить проверку</div>
            <div className="mt-2 text-sm">{error}</div>
          </div>
        )}

        {!loading && !error && audit && (
          <>
            <section className="rounded-2xl bg-white/80 border border-[#3D2D23]/10 p-5">
              <h2 className="font-serif text-xl mb-4">Состав Редакции</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  ["Издания", counts.editions],
                  ["Тома", counts.volumes],
                  ["Материалы", counts.materials],
                  ["Блоки", counts.blocks],
                  ["Snapshots", counts.snapshots],
                  ["Опубликовано", counts.published_materials],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-[#3D2D23]/10 p-4 bg-[#FBF7F0]">
                    <div className="text-xs uppercase tracking-wider opacity-55">{label}</div>
                    <div className="font-serif text-3xl mt-1">{value ?? 0}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl bg-white/80 border border-[#3D2D23]/10 p-5">
              <h2 className="font-serif text-xl">PII-проверка</h2>
              <div className="mt-2 text-sm">
                {pii.length === 0 ? "PII-like поля не обнаружены." : `Обнаружено: ${pii.length}`}
              </div>
              {pii.length > 0 && (
                <div className="mt-3 rounded-xl bg-[#FBF7F0] p-3 text-xs break-all space-y-1">
                  {pii.map((item) => <div key={item}>{item}</div>)}
                </div>
              )}
            </section>

            <section className="rounded-2xl bg-white/80 border border-[#3D2D23]/10 p-5">
              <h2 className="font-serif text-xl">Private Media</h2>
              <div className="mt-2 text-sm">
                {media.length === 0 ? "Private media references не обнаружены." : `Обнаружено путей: ${media.length}`}
              </div>

              {media.length > 0 && (
                <>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="editorial-media-inventory-summary">
                    {[
                      ["Всего ссылок", mediaSummary.total_references],
                      ["Уникальных файлов", mediaSummary.unique_assets],
                      ["Отсутствует", mediaSummary.missing_assets],
                      ["Нужно перенести в RU", mediaSummary.assets_requiring_ru_migration],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-xl border border-[#3D2D23]/10 p-3 bg-[#FBF7F0]">
                        <div className="text-[10px] uppercase tracking-wider opacity-55">{label}</div>
                        <div className="font-serif text-2xl mt-1">{value ?? 0}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 space-y-3" data-testid="editorial-media-inventory">
                    {mediaInventory.map((asset) => (
                      <div key={asset.media_id} className="rounded-xl border border-[#3D2D23]/10 bg-[#FBF7F0] p-4">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="font-medium break-all">{asset.filename || asset.media_id}</div>
                            <div className="mt-1 text-xs opacity-60 break-all">ID: {asset.media_id}</div>
                          </div>
                          <div className={`text-xs font-medium ${asset.asset_exists ? "text-emerald-700" : "text-red-700"}`}>
                            {asset.asset_exists ? "Файл найден" : "Файл отсутствует"}
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <div><span className="opacity-55">Тип:</span> {asset.media_type || "—"}</div>
                          <div><span className="opacity-55">Размер:</span> {asset.size_bytes != null ? `${asset.size_bytes} B` : "—"}</div>
                          <div>
                            <span className="opacity-55">Габариты:</span>{" "}
                            {asset.dimensions?.width || asset.dimensions?.height
                              ? `${asset.dimensions?.width ?? "?"}×${asset.dimensions?.height ?? "?"}`
                              : "—"}
                          </div>
                          <div><span className="opacity-55">Ссылок:</span> {asset.reference_count ?? 0}</div>
                          <div><span className="opacity-55">Хранилище:</span> {asset.storage_class || "—"}</div>
                          <div><span className="opacity-55">Длительность:</span> {asset.duration_seconds != null ? `${asset.duration_seconds} c` : "—"}</div>
                          <div><span className="opacity-55">Media record:</span> {asset.record_exists ? "да" : "нет"}</div>
                          <div>
                            <span className="opacity-55">Binary:</span>{" "}
                            {asset.binary_exists === true ? "да" : asset.binary_exists === false ? "нет" : "не проверяется"}
                          </div>
                        </div>

                        {asset.referenced_paths?.length > 0 && (
                          <details className="mt-3">
                            <summary className="cursor-pointer text-xs font-medium">
                              Где используется ({asset.referenced_paths.length})
                            </summary>
                            <div className="mt-2 rounded-lg bg-white/70 p-2 text-[11px] break-all space-y-1 max-h-40 overflow-auto">
                              {asset.referenced_paths.map((path) => <div key={path}>{path}</div>)}
                            </div>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>

                  <details className="mt-4">
                    <summary className="cursor-pointer text-xs font-medium">Технические пути media references</summary>
                    <div className="mt-2 rounded-xl bg-[#FBF7F0] p-3 text-xs break-all space-y-1 max-h-64 overflow-auto">
                      {media.map((item) => <div key={item}>{item}</div>)}
                    </div>
                  </details>
                </>
              )}
            </section>

            <section className="rounded-2xl bg-white/80 border border-[#3D2D23]/10 p-5">
              <h2 className="font-serif text-xl">Итог</h2>
              <div className="mt-2 text-sm space-y-1">
                <div>Safe for content export: <strong>{audit.safe_for_content_export ? "ДА" : "НЕТ"}</strong></div>
                <div>Требуется media remap: <strong>{audit.requires_media_remap ? "ДА" : "НЕТ"}</strong></div>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
